import { Box, Colors, Spinner } from '@factorialco/deprecated-design-system'
import { endOfISOWeek, format, startOfISOWeek } from 'date-fns'
import { EngagementEmployeePulseSummaryFilter } from 'generated/resources/datatable.graphql'
import { TopicsChartDocument, TopicsChartQuery } from 'generated/resources/graphql'

import BarChart from 'modules/core/components/BarChart'
import useFilters from 'modules/core/components/DataTable/hooks/useFilters'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { ChartSkeleton } from 'modules/engagement/components/ChartSkeleton'

type EngagementEmployeePulseTopicInsight = NonNullable<
  TopicsChartQuery['engagement']['employeePulseTopicInsightsConnection']['edges'][number]['node']
>

// Mood categories ordered from most negative to most positive
const MOOD_CATEGORIES = ['struggling', 'notGreat', 'okay', 'good', 'great'] as const

const MOOD_COLORS = [
  'moodSuperNegative',
  'moodNegative',
  'moodNeutral',
  'moodPositive',
  'moodSuperPositive',
] as const satisfies Colors[]

const getMoodTranslation = (mood: (typeof MOOD_CATEGORIES)[number]): string => {
  switch (mood) {
    case 'struggling':
      return i18n.t('engagement.employee_pulse_labels.1')
    case 'notGreat':
      return i18n.t('engagement.employee_pulse_labels.2')
    case 'okay':
      return i18n.t('engagement.employee_pulse_labels.3')
    case 'good':
      return i18n.t('engagement.employee_pulse_labels.4')
    case 'great':
      return i18n.t('engagement.employee_pulse_labels.5')
    default:
      return mood
  }
}

const muncher = (data: EngagementEmployeePulseTopicInsight[]) => {
  return data.map((node) => {
    const computedNode: Record<string, number | string> = {
      bucket: node.topicName,
    }

    // Initialize all mood categories with 0
    MOOD_CATEGORIES.forEach((category) => {
      const translatedCategory = getMoodTranslation(category)
      computedNode[translatedCategory] = 0
    })

    // Map values by mood category
    const valuesByCategory = new Map<string, number>()
    node.categories.forEach((category, index) => {
      const value = node.series[index]
      if (value) {
        valuesByCategory.set(category, value)
      }
    })

    // Assign values to the corresponding mood categories (1 -> struggling, 2 -> notGreat, ...)
    Array.from(valuesByCategory.entries()).forEach(([category, value]) => {
      const moodIndex = Number(category) - 1
      if (moodIndex >= 0 && moodIndex < MOOD_CATEGORIES.length) {
        const moodCategory = MOOD_CATEGORIES[moodIndex]
        if (moodCategory) {
          const translatedCategory = getMoodTranslation(moodCategory)
          computedNode[translatedCategory] = value
        }
      }
    })

    return computedNode
  })
}

const truncateText = (text: string, totalItems: number): string => {
  let maxLength: number

  if (totalItems <= 7) maxLength = 20
  else if (totalItems <= 10) maxLength = 15
  else if (totalItems <= 15) maxLength = 12
  else if (totalItems <= 20) maxLength = 10
  else maxLength = 8

  if (text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}

const getOptimalFontSize = (totalItems: number): number => {
  if (totalItems <= 8) return 10
  else if (totalItems <= 12) return 9
  else if (totalItems <= 16) return 8
  else return 7
}

interface ChartProp {
  startDate: Date
  endDate: Date
}

const Chart = ({ startDate, endDate }: ChartProp) => {
  const { getFiltersVariables } = useFilters<EngagementEmployeePulseSummaryFilter>()
  const filters: EngagementEmployeePulseSummaryFilter = getFiltersVariables().filter || {}
  const { data, loading: isChartLoading } = useQuery(TopicsChartDocument, {
    variables: {
      from: format(startOfISOWeek(startDate), 'yyyy-MM-dd'),
      to: format(endOfISOWeek(endDate), 'yyyy-MM-dd'),
      managerId: filters?.managerId?.in || [],
      values: filters?.value?.in || undefined,
      teamId: filters?.teamId?.in || undefined,
      tags: filters?.tag?.in || undefined,
    },
  })

  const allSeries =
    data?.engagement.employeePulseTopicInsightsConnection.edges
      .map(({ node }) => node.series)
      .flat() || []

  const isEmpty = allSeries.reduce((sum, current) => sum + current, 0) === 0
  const hasRecords = !!data?.engagement.employeePulseTopicInsightsConnection.totalCount

  const munchedData = muncher(
    data?.engagement.employeePulseTopicInsightsConnection.edges.map(({ node }) => node) || []
  )

  const nodes =
    data?.engagement.employeePulseTopicInsightsConnection.edges.map(({ node }) => node) || []

  const highestSum =
    nodes.length > 0
      ? Math.max(...nodes.map((node) => node.series.reduce((sum, value) => sum + value, 0)))
      : 0

  const translatedKeys = MOOD_CATEGORIES.map(getMoodTranslation)

  return (
    <>
      {isChartLoading ? (
        <Box width="full" alignItems="center" justifyContent="center" height="full">
          <Spinner />
        </Box>
      ) : (
        <>
          {isEmpty || !hasRecords ? (
            <ChartSkeleton hasRecords={hasRecords} />
          ) : (
            <BarChart
              margin={{ top: 40, bottom: 60, left: 30, right: 0 }}
              indexBy="bucket"
              colors={MOOD_COLORS}
              axisBottom={{
                format: (label: string) => label,
                renderTick: ({ x, y, value }) => {
                  const truncatedValue = truncateText(String(value), munchedData.length)
                  const fontSize = getOptimalFontSize(munchedData.length)

                  return (
                    <g transform={`translate(${x},${y + 28})`}>
                      <text
                        textAnchor="middle"
                        dominantBaseline="middle"
                        style={{
                          fontFamily: 'Inter',
                          fontSize: fontSize,
                          cursor: 'help',
                        }}
                        y={-8}
                      >
                        <title>{String(value)}</title>
                        {truncatedValue}
                      </text>
                    </g>
                  )
                },
              }}
              maxValue={highestSum}
              keys={translatedKeys}
              data={munchedData}
              enabledGridY
              groupMode="stacked"
              selectableBars={false}
            />
          )}
        </>
      )}
    </>
  )
}

export default Chart
